// Port of Flutter's signUpScreen.dart.
//
// Field order, the Alexa note under the email box, the Terms checkbox and the
// WhatsApp OTP confirmation are all as they are in the Dart. Registration only
// fires AFTER the OTP matches, then the new account is logged straight in.
//
// NOTE: the submit button's label reads "Login" — that is what the Flutter
// screen says (signUpScreen.dart line ~428), kept as-is so the two apps look
// identical. Change it in both if you want it to read "Sign Up".

import React, { useState } from 'react';
import {
  Box, Typography, Checkbox, Link, IconButton, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, useTheme,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import MailIcon from '@mui/icons-material/Mail';
import PhoneIcon from '@mui/icons-material/Phone';
import PasswordIcon from '@mui/icons-material/Password';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import GlassCard from '../components/GlassCard';
import AuthField from '../components/AuthField';
import AuthButton from '../components/AuthButton';
import { useMessage } from '../components/MessageDialog';
import { useApp } from '../../../core/state/AppContext';
import { registerNewUser } from '../../../core/api/api';
import { requestOtp, attemptLogin } from '../../../core/auth/loginFlow';
import { getDeviceId } from '../../../core/utils/deviceId';
import { PRIVACY_POLICY_URL, WHATSAPP_URL, openExternal } from '../../../core/constants';

const HEADING_FONT = "'BetterFonts', 'Satisfy', cursive";

const TERMS_TEXT = `We collect your mobile number for:

• Verifying your identity via OTP
• Protecting your account from fraud
• Ensuring secure login

Your mobile number *will not be used for marketing* or third-party sharing.`;

export default function MobileSignUpScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { setSession } = useApp();
  const { show } = useMessage();

  const [first, setFirst] = useState('');
  const [middle, setMiddle] = useState('');
  const [last, setLast] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [pin, setPin] = useState('');
  const [hidePin, setHidePin] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);

  const [otp, setOtp] = useState(null);
  const [otpInput, setOtpInput] = useState('');

  const submit = async () => {
    // Not ticked? Flutter opens the policy page instead of registering.
    if (!agreed) { openExternal(PRIVACY_POLICY_URL); return; }

    if (!first || !middle || !last || !email || !mobile || !pin) {
      show('Missing Details',
        'Please enter all the detials mentioned above in order to get your account registered.');
      return;
    }
    if (mobile.length !== 10) {
      show('Invalid Number', 'Number must be 10 digits long.');
      return;
    }
    if (pin.length < 4) {
      show('Invalid PIN', 'The PIN is invalid. Please Enter a valid PIN.');
      return;
    }

    setBusy(true);
    const code = await requestOtp(mobile);
    setBusy(false);
    setOtpInput('');
    setOtp(code);
  };

  const confirm = async () => {
    if (otpInput !== String(otp ?? '')) { setOtp(null); return; }
    setBusy(true);
    try {
      const deviceName = await getDeviceId();
      await registerNewUser({
        name: first, middleName: middle, lastName: last,
        password: pin, email, mobile, deviceName,
      });
      const result = await attemptLogin(mobile, pin);
      if (result.status === 'ok') {
        setSession(result.userData);
        navigate('/home', { replace: true });
      } else {
        show('Registered', 'Your account was created. Please log in.');
        navigate('/login', { replace: true });
      }
    } catch (e) {
      show('Registration Failed', 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
      setOtp(null);
    }
  };

  return (
    <GlassCard>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 32, color: c.text, fontWeight: 700, fontFamily: HEADING_FONT }}>
          Sign Up
        </Typography>

        <Box sx={{ height: 45 }} />
        <AuthField placeholder="First Name" icon={<PersonIcon />}
          value={first} onChange={(e) => setFirst(e.target.value)} />

        <Box sx={{ height: 45 }} />
        <Box sx={{ display: 'flex', gap: 2.5, width: '100%' }}>
          <AuthField placeholder="Middle Name"
            value={middle} onChange={(e) => setMiddle(e.target.value)} />
          <AuthField placeholder="Last Name"
            value={last} onChange={(e) => setLast(e.target.value)} />
        </Box>

        <Box sx={{ height: 45 }} />
        <AuthField placeholder="E-Mail Id" icon={<MailIcon />}
          value={email} onChange={(e) => setEmail(e.target.value)} />
        <Typography sx={{ color: c.text, fontSize: 12, fontStyle: 'italic', alignSelf: 'flex-start' }}>
          This email will be used for Alexa voice control.
        </Typography>

        <Box sx={{ height: 30 }} />
        <AuthField placeholder="Mobile Number" icon={<PhoneIcon />} maxLength={10}
          value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric" />

        <Box sx={{ height: 20 }} />
        <AuthField
          placeholder="Pin"
          icon={<PasswordIcon />}
          maxLength={4}
          type={hidePin ? 'password' : 'text'}
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          inputMode="numeric"
          endAdornment={(
            <InputAdornment position="end">
              <IconButton onClick={() => setHidePin((v) => !v)} sx={{ color: c.text }}>
                <VisibilityIcon />
              </IconButton>
            </InputAdornment>
          )}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', py: 1.25 }}>
          <Checkbox
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            sx={{ color: '#BDBDBD', '&.Mui-checked': { color: '#9E9E9E' } }}
          />
          <Typography sx={{ color: c.text, fontSize: 16 }}>
            I agree to the{' '}
            <Link
              component="button"
              type="button"
              onClick={() => show('Terms And Conditions', TERMS_TEXT)}
              sx={{ color: c.text, fontSize: 16, textDecorationColor: c.text }}
            >
              Terms And Conditions
            </Link>
          </Typography>
        </Box>

        <AuthButton onClick={submit} disabled={busy}>Login</AuthButton>

        <Box sx={{ height: 20 }} />
        <Typography sx={{ color: c.text, fontSize: 16 }}>
          {'Already have an account? '}
          <Link
            component="button"
            type="button"
            onClick={() => navigate('/login')}
            sx={{ color: c.text, fontSize: 16, textDecorationColor: c.text }}
          >
            Login
          </Link>
        </Typography>
      </Box>

      <Dialog open={otp != null} onClose={() => setOtp(null)}
        PaperProps={{ sx: { borderRadius: '15px' } }}>
        <DialogTitle>Enter OTP received on WhatsApp</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth placeholder="Type here..."
            value={otpInput} onChange={(e) => setOtpInput(e.target.value)}
            inputMode="numeric"
          />
          <Typography sx={{ mt: 1.5, fontSize: 16, fontWeight: 700 }}>
            {'Get OTP from '}
            <Link component="button" type="button"
              onClick={() => openExternal(WHATSAPP_URL)} sx={{ fontSize: 16 }}>
              WhatsApp
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOtp(null)}>Cancel</Button>
          <Button variant="contained" onClick={confirm} disabled={busy}>Submit</Button>
        </DialogActions>
      </Dialog>
    </GlassCard>
  );
}
